import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { OPEN_AI_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import OpenAPI from 'dashboard/api/integrations/openapi';

const AI_PROVIDER_IDS = ['openai', 'perplexity'];
const activeAIProviderId = ref(null);

const cleanLabels = labels => {
  return labels
    .toLowerCase()
    .split(',')
    .filter(label => label.trim())
    .map(label => label.trim())
    .filter((label, index, self) => self.indexOf(label) === index);
};

export function useAI() {
  const store = useStore();
  const getters = useStoreGetters();
  const { t } = useI18n();

  const uiFlags = computed(() => getters['integrations/getUIFlags'].value);

  const appIntegrations = computed(
    () => getters['integrations/getAppIntegrations'].value || []
  );
  const currentChat = computed(() => getters.getSelectedChat.value);
  const replyMode = computed(
    () => getters['draftMessages/getReplyEditorMode'].value
  );

  const aiProvidersList = ref([]);
  const isInitialized = ref(false);

  const syncProviders = integrations => {
    const providers = integrations
      .filter(
        integration =>
          AI_PROVIDER_IDS.includes(integration.id) &&
          integration.hooks &&
          integration.hooks.length
      )
      .map(integration => ({
        id: integration.id,
        name: integration.name,
        hook: integration.hooks[0],
      }));

    // Only update if providers actually changed
    if (JSON.stringify(aiProvidersList.value) === JSON.stringify(providers)) {
      return;
    }

    aiProvidersList.value = providers;
    isInitialized.value = true;

    if (!providers.length) {
      activeAIProviderId.value = null;
      return;
    }

    const existing = providers.find(
      provider => provider.id === activeAIProviderId.value
    );

    if (!existing) {
      // Prefer Perplexity as default if available, otherwise use first provider
      const perplexity = providers.find(p => p.id === 'perplexity');
      activeAIProviderId.value = perplexity ? perplexity.id : providers[0].id;
    }
  };

  // Lazy initialization - only sync when integrations are loaded
  watch(
    appIntegrations,
    newIntegrations => {
      if (newIntegrations.length > 0) {
        syncProviders(newIntegrations);
      }
    },
    { immediate: true }
  );

  const aiProviders = computed(() => aiProvidersList.value);

  const aiIntegration = computed(() => {
    const provider = aiProviders.value.find(
      candidate => candidate.id === activeAIProviderId.value
    );

    if (!provider) {
      return null;
    }

    return {
      ...provider.hook,
      provider_id: provider.id,
      provider_name: provider.name,
    };
  });

  const aiProviderId = computed(() => aiIntegration.value?.provider_id || null);

  const providerTranslationKey = computed(() => {
    if (aiProviderId.value === 'perplexity') {
      return 'PERPLEXITY_AI';
    }

    return 'OPEN_AI';
  });

  const providerDisplayName = computed(
    () => aiIntegration.value?.provider_name || ''
  );

  const isAIIntegrationEnabled = computed(() => {
    return !!aiIntegration.value;
  });

  const isLabelSuggestionFeatureEnabled = computed(() => {
    if (aiIntegration.value) {
      const { settings = {} } = aiIntegration.value || {};
      return settings.label_suggestion;
    }
    return false;
  });

  const isFetchingAppIntegrations = computed(() => uiFlags.value.isFetching);

  const hookId = computed(() => aiIntegration.value?.id);

  const conversationId = computed(() => currentChat.value?.id);

  const draftKey = computed(
    () => `draft-${conversationId.value}-${replyMode.value}`
  );

  const draftMessage = computed(() =>
    getters['draftMessages/get'].value(draftKey.value)
  );

  const fetchIntegrations = async () => {
    await store.dispatch('integrations/get');
    // Ensure isFetching is reset after integrations are loaded
    store.commit('integrations/SET_INTEGRATIONS_UI_FLAG', {
      isFetching: false,
    });
    syncProviders(store.state.integrations.records || []);
  };

  const recordAnalytics = async (type, payload) => {
    const event = OPEN_AI_EVENTS[type.toUpperCase()];
    if (event) {
      useTrack(event, {
        type,
        ...payload,
      });
    }
  };

  const fetchLabelSuggestions = async () => {
    if (!conversationId.value) return [];

    try {
      const result = await OpenAPI.processEvent({
        type: 'label_suggestion',
        hookId: hookId.value,
        conversationId: conversationId.value,
      });

      const {
        data: { message: labels },
      } = result;

      return cleanLabels(labels);
    } catch {
      return [];
    }
  };

  const processEvent = async (type = 'rephrase') => {
    try {
      const result = await OpenAPI.processEvent({
        hookId: hookId.value,
        type,
        content: draftMessage.value,
        conversationId: conversationId.value,
      });
      const {
        data: { message: generatedMessage },
      } = result;
      return generatedMessage;
    } catch (error) {
      const errorData = error.response?.data?.error;
      const errorMessage =
        errorData?.error?.message ||
        t(
          `INTEGRATION_SETTINGS.${providerTranslationKey.value}.GENERATE_ERROR`
        );
      useAlert(errorMessage);
      return '';
    }
  };

  onMounted(() => {
    fetchIntegrations();
  });

  const setAIProvider = providerId => {
    if (aiProviders.value.find(provider => provider.id === providerId)) {
      activeAIProviderId.value = providerId;
    }
  };

  return {
    draftMessage,
    uiFlags,
    appIntegrations,
    currentChat,
    replyMode,
    isAIIntegrationEnabled,
    isLabelSuggestionFeatureEnabled,
    isFetchingAppIntegrations,
    fetchIntegrations,
    fetchIntegrationsIfRequired: fetchIntegrations,
    recordAnalytics,
    fetchLabelSuggestions,
    processEvent,
    aiProviders,
    aiProviderId,
    providerTranslationKey,
    providerDisplayName,
    activeAIProviderId,
    setAIProvider,
  };
}
